// Lógica pura de los mensajes con la distribuidora (decisión D53): el orden, los días, el aviso de no leídos y las
// tildes. Sin React ni red: se prueba sola (chat-logica.test.ts).
import { hoyISO, sumarDias, ZONA_HORARIA } from '../compartido';
import type { EventoSeguimiento, LecturaDistribuidora, MensajeTienda } from './chat-tipos';
import { fechaLarga } from './formato';

/** Inserta o reemplaza por id y deja ordenado por llegada (los que todavía están en el celular, al final). */
export function insertarMensaje(lista: readonly MensajeTienda[], m: MensajeTienda): MensajeTienda[] {
  const i = lista.findIndex((x) => x.id === m.id);
  const copia = lista.slice();
  if (i >= 0) copia[i] = m;
  else copia.push(m);
  return copia.sort((a, b) => (a.seq || Number.MAX_SAFE_INTEGER) - (b.seq || Number.MAX_SAFE_INTEGER));
}

export function juntarMensajes(lista: readonly MensajeTienda[], nuevos: readonly MensajeTienda[]): MensajeTienda[] {
  return nuevos.reduce<MensajeTienda[]>((l, m) => insertarMensaje(l, m), lista.slice());
}

export type Tildes = 'enviado' | 'recibido' | 'leido';

/** Lo propio: leído si alguien de la distribuidora lo leyó; recibido si le llegó. */
export function tildesDe(m: Pick<MensajeTienda, 'seq'>, d: LecturaDistribuidora | null | undefined): Tildes {
  if (!d || !m.seq) return 'enviado';
  if (d.leido_seq >= m.seq) return 'leido';
  if (d.recibido_seq >= m.seq) return 'recibido';
  return 'enviado';
}

/** "2026-10-20" del instante en la zona de la empresa. */
export const diaDe = (iso: string, zona = ZONA_HORARIA): string => hoyISO(zona, new Date(iso));

/** Separador: "Hoy", "Ayer" o "lunes 19 de octubre". */
export function etiquetaDia(fecha: string, ahora = new Date(), zona = ZONA_HORARIA): string {
  const hoy = hoyISO(zona, ahora);
  if (fecha === hoy) return 'Hoy';
  if (fecha === sumarDias(hoy, -1)) return 'Ayer';
  const larga = fechaLarga(fecha);
  return larga.charAt(0).toUpperCase() + larga.slice(1);
}

export type Elemento =
  | { tipo: 'dia'; clave: string; etiqueta: string }
  | { tipo: 'no_leidos'; clave: string; cantidad: number }
  | { tipo: 'mensaje'; clave: string; mensaje: MensajeTienda; primero: boolean };

/**
 * Lo que cuenta como novedad para el comercio: lo que escribió la distribuidora y los avisos de lo que hizo ella o el
 * reparto (no "Recibimos tu pedido" ni lo que modificó o canceló él mismo).
 */
export const esNovedad = (m: MensajeTienda): boolean =>
  !m.propio && !m.eliminado && (m.de === 'distribuidora' || (m.de === 'sistema' && !!m.aviso));

/**
 * Lo que se dibuja: separadores por día, "N mensajes nuevos" antes del primero sin leer (lo que escribió la
 * distribuidora después de `leidoHasta`) y cada mensaje marcado si es el primero de una seguidilla del mismo lado.
 */
export function armarConversacion(
  mensajes: readonly MensajeTienda[],
  opciones: { leidoHasta?: number | null; ahora?: Date; zona?: string } = {},
): Elemento[] {
  const { leidoHasta, ahora = new Date(), zona = ZONA_HORARIA } = opciones;
  const sinLeer = leidoHasta == null ? [] : mensajes.filter((m) => m.seq > leidoHasta && esNovedad(m));
  const primeroSinLeer = sinLeer[0]?.id;
  const r: Elemento[] = [];
  let dia = '';
  let previo: MensajeTienda | null = null;
  for (const m of mensajes) {
    const d = diaDe(m.creado_en, zona);
    let corte = false;
    if (d !== dia) {
      r.push({ tipo: 'dia', clave: `dia-${d}`, etiqueta: etiquetaDia(d, ahora, zona) });
      dia = d;
      corte = true;
    }
    if (m.id === primeroSinLeer) {
      r.push({ tipo: 'no_leidos', clave: 'no-leidos', cantidad: sinLeer.length });
      corte = true;
    }
    const sigue = !corte && previo !== null && previo.de === m.de && m.de !== 'sistema' && previo.autor === m.autor;
    r.push({ tipo: 'mensaje', clave: m.id, mensaje: m, primero: !sigue });
    previo = m;
  }
  return r;
}

/** Ícono de cada aviso del pedido. */
export const ICONO_EVENTO: Record<EventoSeguimiento, string> = {
  recibido: 'receipt_long',
  modificado: 'edit_note',
  aprobado: 'task_alt',
  rechazado: 'block',
  cancelado: 'cancel',
  sale_con_cambios: 'rule',
  en_camino: 'local_shipping',
  entregado: 'check_circle',
  entregado_parcial: 'rule',
  no_entregado: 'error',
  anulado: 'block',
  reprogramado: 'event_repeat',
};

/** Una línea para el push o la vista previa. */
export function vistaPrevia(m: Pick<MensajeTienda, 'tipo' | 'texto' | 'eliminado'>): string {
  if (m.eliminado) return 'Mensaje eliminado';
  const t = (m.texto ?? '').replace(/\s+/g, ' ').trim();
  if (m.tipo === 'imagen') return t ? `Foto: ${t}` : 'Foto';
  return t;
}
